/**
 * Curated Light / Dark Canvas presets — a shared design-system value (see
 * `_docs/architecture/theme-system.md` §10). A "Canvas preset" is a bundle of
 * already-Canvas-owned per-Workspace fields:
 *   `{ appearance: { canvasColor }, grid: { visible, spacing, style, lineWidth, opacity, color } }`
 *
 * Presets are consulted ONLY at new-Workspace creation (App Theme "Match App
 * Theme", or a `saved-default` with nothing saved). The chosen preset is cloned,
 * normalized, and copied into `workspace.appearance` + `workspace.grid`, and is
 * independent forever after — there is no ongoing binding.
 *
 * `getCanvasPreset` always returns a FRESH MUTABLE CLONE. The source constants
 * are deep-frozen and never exported.
 */

import { cloneWorkspaceValue } from './cloneWorkspaceValue';

function deepFreeze(obj) {
  if (obj && typeof obj === 'object') {
    for (const key of Object.keys(obj)) deepFreeze(obj[key]);
    Object.freeze(obj);
  }
  return obj;
}

const _LIGHT = deepFreeze({
  appearance: { canvasColor: '#f6f7f4' }, // curated warm near-white
  grid: { visible: true, spacing: 100, style: 'solid', lineWidth: 1, opacity: 0.06, color: 'neutral' },
});

const _DARK = deepFreeze({
  appearance: { canvasColor: '#0e1a33' }, // curated navy (the long-standing default)
  grid: { visible: true, spacing: 100, style: 'solid', lineWidth: 1, opacity: 0.06, color: 'white' },
});

/**
 * @param {'light'|'dark'} resolvedTheme
 * @returns {{ appearance: { canvasColor: string }, grid: object }} a fresh clone
 */
export function getCanvasPreset(resolvedTheme) {
  return cloneWorkspaceValue(resolvedTheme === 'light' ? _LIGHT : _DARK);
}
